/**
 * LandingPage
 * Professional cover/landing page for the app
 */

import { useNavigate } from 'react-router-dom'
import { Logo, AppFooter } from '@/components'
import { Button } from '@/components/ui'
import {
  BookOpen,
  Edit,
  Download,
  Save,
  Sparkles,
  Check,
  ArrowRight,
  Users,
  Globe,
  Zap,
} from 'lucide-react'

export function LandingPage() {
  const navigate = useNavigate()

  const features = [
    {
      icon: Edit,
      title: 'Distraction-Free Writing',
      description: 'Clean, intuitive editor designed for focused writing without interruptions.',
    },
    {
      icon: BookOpen,
      title: 'Chapter Management',
      description: 'Organize your book with easy-to-manage chapters and seamless navigation.',
    },
    {
      icon: Save,
      title: 'Auto-Save',
      description: 'Never lose your work with automatic saving every 2 seconds.',
    },
    {
      icon: Download,
      title: 'Multiple Export Formats',
      description: 'Export to PDF, EPUB, Markdown, and HTML with a single click.',
    },
    {
      icon: Sparkles,
      title: 'Beautiful UI',
      description: 'Modern, responsive design that works perfectly on all devices.',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Instant loading and smooth performance for uninterrupted creativity.',
    },
  ]

  const stats = [
    { icon: Users, value: '10,000+', label: 'Active Writers' },
    { icon: BookOpen, value: '50,000+', label: 'Books Created' },
    { icon: Globe, value: '150+', label: 'Countries' },
  ]

  const benefits = [
    'No credit card required',
    'Free forever plan',
    'Export in multiple formats',
    'Cloud storage included',
    'Mobile responsive',
    'Regular updates',
  ]

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-200 sticky top-0 z-30 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo size="md" showText={true} />

            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="hidden sm:inline-flex text-sm"
                size="sm"
              >
                Sign In
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/signup')}
                size="sm"
                className="text-sm sm:text-base whitespace-nowrap"
              >
                <span className="hidden xs:inline">Get Started</span>
                <span className="xs:hidden">Start</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="flex-1 pt-16 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Write Your Ebook,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Your Way
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              A platform that makes it easier for regular people to write and create ebooks.
              Start writing today with our intuitive editor, organize your thoughts, and export in
              any format.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Button
                variant="primary"
                size="md"
                onClick={() => navigate('/signup')}
                className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                Start Writing Free
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                Sign In
              </Button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              No credit card required • Free forever • Export unlimited books
            </p>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <div
                  key={index}
                  className="text-center p-6 rounded-lg bg-gradient-to-br from-blue-50 to-purple-50"
                >
                  <Icon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Write Your Ebook
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed specifically for writers who want to focus on creating
              great content.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Start Creating Your Ebook Today
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Join thousands of writers who trust Ebook Genius to bring their stories to life.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-left">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              <Button
                variant="secondary"
                size="md"
                onClick={() => navigate('/signup')}
                className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-600 hover:bg-gray-100"
              >
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>
  )
}
